import { useState } from "react";
import { format } from "date-fns";
import { CalendarCheck } from "lucide-react";
import PageShell from "../components/PageShell";
import TextField from "../components/TextField";
import FormStatus from "../components/FormStatus";
import useSeo from "../hooks/useSeo";
import { Calendar } from "../components/ui/calendar";
import { api } from "../lib/api";
import { serviceOptions, timeSlots } from "../data/content";

const initialState = { name: "", email: "", company: "", service: serviceOptions[0], notes: "" };

export default function Booking() {
  useSeo("Booking Calendar", "Book a consultation with TalentBridge Consulting using the built-in appointment calendar.");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState(timeSlots[0]);
  const [form, setForm] = useState(initialState);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const onChange = (event) => setForm({ ...form, [event.target.name]: event.target.value });
  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await api.booking({
        ...form,
        selected_date: format(selectedDate, "yyyy-MM-dd"),
        selected_time: selectedTime,
      });
      setForm(initialState);
      setStatus("success");
    } catch (error) {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <section className="bg-white px-5 pb-16 pt-32 md:px-8 lg:pb-24" data-testid="booking-page-section">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#0066FF]" data-testid="booking-eyebrow">Booking calendar</p>
            <h1 className="mt-5 font-heading text-4xl font-black leading-tight text-[#0A2540] sm:text-5xl" data-testid="booking-title">Reserve a consultation slot.</h1>
            <p className="mt-6 text-base leading-8 text-[#475569]" data-testid="booking-description">
              Select a date and time for a focused advisory conversation with TalentBridge Consulting.
            </p>
          </div>

          <form onSubmit={onSubmit} className="mt-12 grid gap-8 lg:grid-cols-[0.85fr_1.15fr]" data-testid="booking-form">
            <div className="border border-[#E2E8F0] bg-[#FBFBFC] p-5 md:p-7" data-testid="booking-calendar-panel">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                disabled={{ before: new Date() }}
                className="mx-auto"
                data-testid="booking-calendar-picker"
              />
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {timeSlots.map((slot) => (
                  <button
                    type="button"
                    key={slot}
                    onClick={() => setSelectedTime(slot)}
                    className={`border px-4 py-3 text-sm font-bold transition-colors ${selectedTime === slot ? "border-[#0066FF] bg-[#0066FF] text-white" : "border-[#CBD5E1] bg-white text-[#0A2540] hover:border-[#0066FF]"}`}
                    data-testid={`booking-time-slot-${slot.replace(":", "")}`}
                  >
                    {slot}
                  </button>
                ))}
              </div>
              <p className="mt-6 bg-white p-4 text-sm font-bold text-[#0A2540]" data-testid="booking-selected-slot-text">
                Selected: {selectedDate ? format(selectedDate, "PPP") : "Choose a date"} at {selectedTime}
              </p>
            </div>
            <div className="grid gap-5 border border-[#E2E8F0] bg-white p-6 md:p-8" data-testid="booking-details-panel">
              <FormStatus status={status} successText="Your consultation booking has been saved." />
              <div className="grid gap-5 md:grid-cols-2">
                <TextField id="name" label="Name" value={form.name} onChange={onChange} />
                <TextField id="email" label="Email" type="email" value={form.email} onChange={onChange} />
              </div>
              <TextField id="company" label="Company" value={form.company} onChange={onChange} required={false} />
              <div className="grid gap-2">
                <label htmlFor="service" className="text-xs font-bold uppercase tracking-[0.16em] text-[#475569]" data-testid="service-label">Service</label>
                <select id="service" name="service" value={form.service} onChange={onChange} className="min-h-11 border border-[#CBD5E1] bg-white px-3 text-[#0B1120] focus:outline-none focus:ring-2 focus:ring-[#0066FF]" data-testid="service-input">
                  {serviceOptions.map((option) => (
                    <option value={option} key={option}>{option}</option>
                  ))}
                </select>
              </div>
              <TextField id="notes" label="Notes" multiline value={form.notes} onChange={onChange} required={false} />
              <button type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 bg-[#0A2540] px-6 py-4 font-bold text-white transition-transform hover:-translate-y-0.5 hover:bg-[#051424] disabled:opacity-60" data-testid="booking-submit-button">
                {loading ? "Saving..." : "Confirm booking"} <CalendarCheck className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      </section>
    </PageShell>
  );
}